import { useEffect, useState } from "react";
import { http, formatMoney } from "@/lib/api";
import { getMarket } from "@/config/markets";

/**
 * Shared, in-memory cache for the current venue so multiple dashboard pages
 * don't refetch `/venues/me` on every mount. Cleared on logout.
 */
let cachedVenue = null;
let inflight = null;

export function invalidateVenueMarketCache() {
  cachedVenue = null;
  inflight = null;
}

async function loadVenue() {
  if (cachedVenue) return cachedVenue;
  if (!inflight) {
    inflight = http.get("/venues/me")
      .then((r) => { cachedVenue = r.data; return cachedVenue; })
      .catch((e) => { inflight = null; throw e; });
  }
  return inflight;
}

/**
 * Returns everything a dashboard page needs to render money in the venue's
 * native market: currency code, symbol, formatter, and the full market
 * config (booking fees, caps, rebate, etc.).
 *
 * Falls back to UK/GBP until the venue loads.
 */
export function useVenueMarket() {
  const [venue, setVenue] = useState(cachedVenue);

  useEffect(() => {
    if (cachedVenue) return;
    let cancelled = false;
    loadVenue()
      .then((v) => { if (!cancelled) setVenue(v); })
      .catch(() => { /* stay on defaults */ });
    return () => { cancelled = true; };
  }, []);

  const country = venue?.country || "GB";
  const marketKey = country === "US" ? "us" : "uk";
  const market = getMarket(marketKey);
  const currency = market.currency;       // "GBP" | "USD"
  const symbol = market.symbol;           // "£" | "$"

  return {
    venue,
    country,
    market,
    currency,
    symbol,
    /** Format a value in the smallest currency unit (pence/cents). */
    fmt: (minor) => formatMoney(minor, currency),
    /** Format a value in the major unit (pounds/dollars). */
    fmtMajor: (major, opts) => {
      const dp = opts?.decimals ?? (Number.isInteger(Number(major)) ? 0 : 2);
      const loc = currency === "USD" ? "en-US" : "en-GB";
      const n = Number(major) || 0;
      const formatted = n.toLocaleString(loc, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });
      return `${symbol}${formatted}`;
    },
  };
}
